import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import last from 'lodash/last';

// Constants
import TrackingConstants from 'Shared/constants/Tracking.constants';
import QueryParamConstants from 'Shared/constants/QueryParams.constants';

// Helpers
import { isEmptyString, validEmail, showNotificationError, fullNameFromEmail } from 'Shared/helpers';

// Types
import { WorkspaceType } from 'Shared/constants/types';
import { Illustrations } from 'Wpds/components/illustration';

// Selectors
import { getOrganizationDomain } from 'Shared/selectors/organization';

// Actions
import { bulkCreateWorkspaces } from 'Shared/actions/workspaces';

// Utils
import { useCopyToClipboard } from 'Shared/utils/hooks/use-copy-to-clipboard';
import { useQueryParam } from 'Shared/utils/hooks';
import { useOrganization } from 'Shared/utils/hooks/use-organization';
import useValidateEmployeeEmail from 'Shared/utils/hooks/use-validate-employee-email';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

// Components
import SimpleAddWorkspaceWrapper from 'Shared/components/modals/SimpleAddWorkspaceModals/SimpleAddWorkspaceWrapper/SimpleAddWorkspaceWrapper';
import AddTeammateSuggestionsSection from 'Shared/components/modals/SimpleAddWorkspaceModals/SuggestionsSection/AddTeammateSuggestionsSection/AddTeammateSuggestionsSection';
import AddByEmailNameSection from './AddByEmailNameSection/AddByEmailNameSection';
import { WpIcon } from 'Wpds/components/icon';

type Props = {
  isOpen?: boolean;
  onClose?: Function;
  onSuccess?: Function;
  returnAllResults?: boolean;
  redirectOnSuccess?: boolean;
  submitButtonText?: string;
};

function AddTeammateByEmailModal({
  isOpen = false,
  onClose,
  onSuccess,
  returnAllResults = false,
  redirectOnSuccess = true,
  submitButtonText,
}: Props) {
  const { tr } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const [modalParam, setModalParam] = useQueryParam(QueryParamConstants.INVITE_BY_EMAIL_MODAL);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuggestedListOpened, setIsSuggestedListOpened] = useState(false);
  const inviteLink = useSelector((state: any) => state.user?.inviteLink);
  const { organization, isPiOrganization } = useOrganization();
  const { isEmployeeEmailOrValidDomain } = useValidateEmployeeEmail();
  const organizationDomain = useSelector(getOrganizationDomain);
  const { onCopyTextToClipboard } = useCopyToClipboard({ textToCopy: inviteLink });

  const defaultFormValues = {
    0: {
      fullName: '',
      teammateEmail: organizationDomain,
      userId: null,
      recurringMeetingId: null,
      errorMessage: null,
      fromCalendar: false,
    },
  };
  const [formValues, setFormValues] = useState(defaultFormValues);

  const handleCopyInviteLinkClick = () => {
    window?.analytics?.track?.('Copied Invite Link', { is_group: false });
    onCopyTextToClipboard();
  };

  const validateFields = ({ teammateEmail }) => {
    if (isEmptyString(teammateEmail)) return tr('perform.workspace.simple_bulk_invite.validation.enter_email');
    if (teammateEmail !== organizationDomain && !validEmail(teammateEmail))
      return tr('perform.workspace.simple_bulk_invite.validation.invalid_email', teammateEmail);
    if (!validEmail(teammateEmail) && teammateEmail.startsWith('@'))
      return tr('perform.workspace.simple_bulk_invite.validation.incomplete_email', teammateEmail);
    if (!isEmployeeEmailOrValidDomain({ email: teammateEmail }))
      return tr(
        'perform.workspace.simple_bulk_invite.validation.invalid_domain_list',
        organization.pi_allowed_domains.join(', ')
      );
    return null;
  };

  useEffect(() => {
    setFormValues(defaultFormValues);
  }, [modalParam]);

  const handleSuggestedMeetingClick = ({ recurringMeetingId, userId, email, fullName }) => {
    const lastKey = last(Object.keys(formValues));
    const lastField = formValues[lastKey];
    let newKey;
    if (
      isEmptyString(lastField.fullName) &&
      (isEmptyString(lastField.teammateEmail) || lastField.teammateEmail === organizationDomain)
    ) {
      newKey = lastKey;
    } else {
      newKey = parseInt(lastKey, 10) + 1;
    }

    window?.analytics?.track?.('Clicked in Add Workspace Modal', {
      modal: 'add_teammate_by_email',
      button: 'Meeting',
    });

    setFormValues({
      ...formValues,
      [newKey]: {
        recurringMeetingId,
        teammateEmail: email || '',
        userId,
        fullName: fullName || fullNameFromEmail(email) || '',
        fromCalendar: true,
      },
    });
  };

  const handleClose = () => {
    if (onClose) onClose();
    else setModalParam(null);
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const checkedformValues: any = {};
      Object.keys(formValues).forEach(key => {
        checkedformValues[key] = { ...formValues[key], errorMessage: validateFields(formValues[key]) };
      });
      if (Object.values(checkedformValues).find(({ errorMessage }) => errorMessage)) {
        setFormValues(checkedformValues);
        return null;
      }

      const workspacesParams = Object.values(checkedformValues).map(
        (value: { fullName: string; teammateEmail: string; recurringMeetingId: number; fromCalendar: boolean }) => {
          const inviteeEmail = value.teammateEmail !== organizationDomain ? value.teammateEmail : null;
          return {
            name: value.fullName.length ? value.fullName : inviteeEmail,
            inviteeEmails: [inviteeEmail],
            recurringMeetingId: value.recurringMeetingId,
            inviteeEmailsFromCalendar: value.fromCalendar ? [inviteeEmail] : [null],
          };
        }
      );

      const results = await dispatch(
        bulkCreateWorkspaces({
          workspacesParams,
          inviteMethod: TrackingConstants.INVITE_METHOD_TYPES.INVITE_TEAMMATE_NAVIGATION,
          returnAll: returnAllResults,
        })
      );
      if (results) {
        handleClose();
        if (onSuccess) onSuccess(results);
        else if (redirectOnSuccess) {
          if (Array.isArray(results)) history.push(results[0]?.path);
          else history.push(results?.path);
        }
      }
    } catch (e) {
      showNotificationError(e);
    } finally {
      setIsSubmitting(false);
    }
    return null;
  };

  const disabledSubmit = isSubmitting;

  if (!isOpen && !modalParam) return null;

  return (
    <SimpleAddWorkspaceWrapper
      onClose={handleClose}
      workspaceType={WorkspaceType.isTeammate}
      title={tr('perform.workspace.simple_bulk_invite.title')}
      description={
        isPiOrganization ? null : (
          <div className="font-weight-normal">
            {tr('perform.workspace.add_teammate_by_email.description')}{' '}
            <button type="button" className="btn btn-link px-1" onClick={handleCopyInviteLinkClick}>
              <WpIcon name="link" /> {tr('perform.workspace.simple_add_teammate.copy_invite_link')}
            </button>
          </div>
        )
      }
      disabledSubmit={disabledSubmit}
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit}
      illustration={Illustrations.three_people_in_windows_with_purple_bg}
      createNameSection={
        <AddByEmailNameSection
          formValues={Object.values(formValues) as any}
          isSuggestedListOpened={isSuggestedListOpened}
          onSetFormValues={setFormValues}
          onSetIsSuggestedListOpened={setIsSuggestedListOpened}
        />
      }
      suggestionsSection={
        <AddTeammateSuggestionsSection
          includeOrgMembers={false}
          formValues={formValues}
          onSuggestedMeetingClick={handleSuggestedMeetingClick}
        />
      }
      submitButtonText={submitButtonText}
    />
  );
}

export default AddTeammateByEmailModal;
